import { TypedDocumentNode, gql } from '@apollo/client';
import { faCartShopping } from '@fortawesome/pro-solid-svg-icons';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { NBALogo } from '@sorare/core/src/atoms/icons/NBALogo';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { SportBall } from '@sorare/core/src/atoms/icons/SportBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { glossary, transferMarket } from '@sorare/core/src/lib/glossary';
import { capitalize } from '@sorare/core/src/lib/humanize';
import { getFormattedSport } from '@sorare/core/src/lib/sports';

import { Eligibility_EligibilityRules } from './__generated__/index.graphql';
import challengerUrl from './assets/challenger.png';
import championUrl from './assets/champion.png';
import contenderUrl from './assets/contender.png';

const Wrapper = styled(Vertical).attrs({ center: true })`
  background: var(--c-nd-100);
  border-radius: var(--double-unit);
  padding: var(--double-unit);
`;

const Conditions = styled(Horizontal).attrs({ center: true, gap: 0.5 })`
  flex-wrap: wrap;
`;

const SmallNBALogo = styled(NBALogo)`
  height: var(--intermediate-unit);
`;

const LEAGUES: Record<string, { name: string; pictureUrl: string }> = {
  'seasonal-champions': {
    name: 'Champion',
    pictureUrl: championUrl,
  },
  'seasonal-challengers': {
    name: 'Challenger',
    pictureUrl: challengerUrl,
  },
  'seasonal-contenders': {
    name: 'Contender',
    pictureUrl: contenderUrl,
  },
};

type Props = {
  eligibilityRules: Eligibility_EligibilityRules;
  sports: Sport[];
};
export const Eligibility = ({ eligibilityRules, sports }: Props) => {
  const {
    eligibleRarities,
    eligibleSo5Leagues,
    anyEligibleTeams = [],
  } = eligibilityRules;

  return (
    <Wrapper>
      <BodyS color="var(--c-nd-600)">
        <FormattedMessage
          id="NeonChristmas.explanation.eligibility.title"
          defaultMessage="When buying"
        />
      </BodyS>
      <Conditions>
        <Chip>
          {sports.map((s, i) => (
            <Fragment key={s}>
              {i > 0 && ' /'}
              <SportBall sport={s} />
              {getFormattedSport(s)}
            </Fragment>
          ))}
        </Chip>

        {anyEligibleTeams.length ? (
          <Chip>
            <SmallNBALogo />
            <FormattedMessage
              id="NeonChristmas.explanation.eligibility.selected_teams"
              defaultMessage="Selected teams"
            />
          </Chip>
        ) : null}

        {eligibleSo5Leagues
          ?.filter(l => Object.keys(LEAGUES).includes(l))
          .map(league => (
            <Chip key={league}>
              <img src={LEAGUES[league]!.pictureUrl} alt="" />
              {LEAGUES[league]!.name}
            </Chip>
          ))}
        {eligibleRarities?.map(r => (
          <Chip key={r}>
            <NewScarcityIcon rarity={r as Rarity} size={1.5} />
            {capitalize(r)}
          </Chip>
        ))}
        <Chip>
          <FontAwesomeIcon icon={faCartShopping} />
          <FormattedMessage {...transferMarket.instantBuy} /> /{' '}
          <FormattedMessage {...glossary.auction} />
        </Chip>
      </Conditions>
    </Wrapper>
  );
};

Eligibility.fragments = {
  EligibilityRules: gql`
    fragment Eligibility_EligibilityRules on EligibilityRules {
      id
      anyEligibleTeams {
        slug
      }
      eligibleRarities
      eligibleSo5Leagues
    }
  ` as TypedDocumentNode<Eligibility_EligibilityRules>,
};
